import { ImageCard } from '@/components/commons/image-card'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Stars01Icon, ThumbsDownIcon } from '@/components/ui/icons'
import { ImageBadge } from '@/features/photoset/components/image-badge'
import {
	SpaceImageFrame,
	SpaceImageSelectionInfo,
	SpaceImagesContainer
} from '@/features/photoset/components/space-images-atoms'
import { useImageSelectionApproval } from '@/features/photoset/queries/use-image-selection-approval'
import type { Image, Photoset, PhotosetSpace } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const VettingQaSpaceImages = ({
	images,
	photosetId,
	photosetSpaceId,
	isQaView
}: {
	images: PhotosetSpace['images']
	photosetId: Photoset['id']
	photosetSpaceId: PhotosetSpace['id']
	isQaView: boolean
}) => {
	const { t } = useTranslation()
	const { mutate: imageSelectionApprovalToggle } = useImageSelectionApproval({
		photosetId,
		photosetSpaceId,
		queryKey: [QUERY_KEYS.PHOTOSET_SPACES],
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const onToggleClick = (image: Omit<Image, 'space'>) => {
		imageSelectionApprovalToggle({
			imageId: image.id,
			vetting_qa_approved: !image.vetting_qa_approved
		})
	}

	return (
		<SpaceImagesContainer $gap={4}>
			{images.map((image) => (
				<SpaceImageFrame
					$isSelected={image.is_manually_retouched}
					key={image.id}
				>
					<ImageCard
						badgeType='selection'
						endSlot={
							<SpaceImageSelectionInfo>
								<section className='flex w-full items-center justify-between gap-2'>
									<h4>{image.name}</h4>
									{image.is_manually_retouched && (
										<ImageBadge type='manually_retouched' />
									)}
								</section>
								<ButtonLegacy
									className='w-full'
									disabled={!isQaView}
									onClick={() => onToggleClick(image)}
									size={'sm'}
									type='button'
									variant={
										image.vetting_qa_approved
											? 'secondary'
											: 'secondaryDanger'
									}
								>
									<ThumbsDownIcon />
									{t('common:reject')}
								</ButtonLegacy>
							</SpaceImageSelectionInfo>
						}
						hideImageInfo
						image={image}
						imageNameIcon={
							!image.is_manually_retouched && (
								<Stars01Icon size={20} />
							)
						}
						isSelected={false}
						thumbnail={
							image.ai_retouched_thumbnails?.xs ||
							image.thumbnails?.xs
						}
					/>
				</SpaceImageFrame>
			))}
		</SpaceImagesContainer>
	)
}
